import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { GetBaseURL } from "../components/utils/urlUtils";
import {
  LogOut,
  Calendar,
  Users,
  Building2,
  Video,
  CheckCircle2,
  XCircle,
  Clock,
  Trash2,
  ChevronDown,
  RefreshCw,
  MessageSquare,
  Send,
  Headphones,
} from "lucide-react";

interface DemoBooking {
  _id: string;
  name: string;
  email: string;
  firmName: string | null;
  phone: string | null;
  date: string;
  time: string;
  status: "confirmed" | "cancelled" | "completed" | "no_show";
  notes: string | null;
  createdAt: string;
}

interface SupportMessage {
  sender: "user" | "admin";
  text: string;
  adminEmail?: string;
  createdAt: string;
}

interface SupportTicket {
  _id: string;
  userName: string;
  userEmail: string;
  firmName: string | null;
  subject: string;
  status: "open" | "replied" | "resolved";
  messages: SupportMessage[];
  updatedAt: string;
  createdAt: string;
}

interface Stats {
  totalFirms: number;
  totalUsers: number;
  upcomingDemos: number;
  totalDemos: number;
}

const STATUS_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  confirmed: { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" },
  completed: { bg: "bg-blue-50", text: "text-blue-700", dot: "bg-blue-500" },
  cancelled: { bg: "bg-red-50", text: "text-red-700", dot: "bg-red-500" },
  no_show: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
};

const TICKET_STATUS: Record<string, { bg: string; text: string; dot: string; label: string }> = {
  open: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500", label: "Open" },
  replied: { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500", label: "Replied" },
  resolved: { bg: "bg-slate-100", text: "text-slate-500", dot: "bg-slate-400", label: "Resolved" },
};

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatTime(time: string) {
  const [h, m] = time.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<DemoBooking[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"upcoming" | "all" | "completed" | "cancelled">("upcoming");
  const [actionMenu, setActionMenu] = useState<string | null>(null);

  // Support tickets state
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [ticketFilter, setTicketFilter] = useState<"open" | "all" | "replied" | "resolved">("open");
  const [activeTicket, setActiveTicket] = useState<SupportTicket | null>(null);
  const [adminReply, setAdminReply] = useState("");
  const [sendingReply, setSendingReply] = useState(false);

  const adminToken = localStorage.getItem("kavoa_admin_token");
  const adminEmail = localStorage.getItem("kavoa_admin_email");

  const api = useCallback(() => {
    return axios.create({
      baseURL: GetBaseURL(),
      headers: { "x-admin-token": adminToken || "" },
    });
  }, [adminToken]);

  const fetchData = useCallback(async () => {
    if (!adminToken) {
      navigate("/kavoa-admin/login");
      return;
    }
    try {
      setLoading(true);
      const query =
        filter === "upcoming"
          ? "?upcoming=true"
          : filter === "all"
          ? ""
          : `?status=${filter}`;
      const ticketQuery = ticketFilter === "all" ? "" : `?status=${ticketFilter}`;
      const [bookingsRes, statsRes, ticketsRes] = await Promise.all([
        api().get(`/admin/demo-bookings${query}`),
        api().get("/admin/stats"),
        api().get(`/api/support/admin/tickets${ticketQuery}`),
      ]);
      setBookings(bookingsRes.data.bookings);
      setStats(statsRes.data);
      setTickets(ticketsRes.data.tickets || []);
    } catch (err: any) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("kavoa_admin_token");
        localStorage.removeItem("kavoa_admin_email");
        navigate("/kavoa-admin/login");
      }
    } finally {
      setLoading(false);
    }
  }, [adminToken, filter, ticketFilter, api, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateBooking = async (id: string, status: string) => {
    try {
      await api().patch(`/admin/demo-bookings/${id}`, { status });
      setActionMenu(null);
      fetchData();
    } catch {}
  };

  const deleteBooking = async (id: string) => {
    if (!window.confirm("Delete this booking permanently?")) return;
    try {
      await api().delete(`/admin/demo-bookings/${id}`);
      setActionMenu(null);
      fetchData();
    } catch {}
  };

  const openTicketDetail = async (ticket: SupportTicket) => {
    try {
      const res = await api().get(`/api/support/admin/tickets/${ticket._id}`);
      setActiveTicket(res.data);
    } catch {}
  };

  const handleAdminReply = async () => {
    if (!adminReply.trim() || !activeTicket) return;
    setSendingReply(true);
    try {
      const res = await api().post(
        `/api/support/admin/tickets/${activeTicket._id}/reply`,
        { message: adminReply.trim() }
      );
      setAdminReply("");
      setActiveTicket(res.data);
      fetchData();
    } catch {
    } finally {
      setSendingReply(false);
    }
  };

  const updateTicketStatus = async (id: string, status: string) => {
    try {
      await api().patch(`/api/support/admin/tickets/${id}`, { status });
      if (activeTicket?._id === id) {
        setActiveTicket((prev) => prev ? { ...prev, status: status as any } : null);
      }
      fetchData();
    } catch {}
  };

  const timeAgo = (date: string) => {
    const s = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };

  const handleLogout = () => {
    localStorage.removeItem("kavoa_admin_token");
    localStorage.removeItem("kavoa_admin_email");
    navigate("/kavoa-admin/login");
  };

  if (!adminToken) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-[#0F5132] text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-lg font-bold leading-tight">Kavoa Admin</h1>
              <p className="text-xs text-emerald-200">{adminEmail}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-sm font-medium transition-all"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {[
              { label: "Total Firms", value: stats.totalFirms, icon: Building2, color: "text-emerald-600" },
              { label: "Total Users", value: stats.totalUsers, icon: Users, color: "text-blue-600" },
              { label: "Upcoming Demos", value: stats.upcomingDemos, icon: Video, color: "text-purple-600" },
              { label: "All-Time Demos", value: stats.totalDemos, icon: Calendar, color: "text-amber-600" },
              { label: "Open Tickets", value: tickets.filter((t) => t.status === "open").length, icon: Headphones, color: "text-red-500" },
            ].map((stat) => (
              <div
                key={stat.label}
                className="bg-white rounded-xl border border-slate-200 p-5"
              >
                <div className="flex items-center justify-between mb-3">
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
                <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                <div className="text-xs text-slate-500 mt-0.5">{stat.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Bookings Section */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-lg font-bold text-slate-900">Demo Bookings</h2>
            <div className="flex items-center gap-2">
              {/* Filter tabs */}
              {(["upcoming", "all", "completed", "cancelled"] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all capitalize ${
                    filter === f
                      ? "bg-[#0F5132] text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {f}
                </button>
              ))}
              <button
                onClick={fetchData}
                className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-all ml-1"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block w-8 h-8 border-3 border-slate-200 border-t-[#0F5132] rounded-full animate-spin" />
            </div>
          ) : bookings.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No bookings found</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {bookings.map((booking) => {
                const sc = STATUS_COLORS[booking.status] || STATUS_COLORS.confirmed;
                return (
                  <div
                    key={booking._id}
                    className="px-6 py-4 flex items-center gap-4 hover:bg-slate-50 transition-colors"
                  >
                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-full bg-[#0F5132] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {booking.name.charAt(0).toUpperCase()}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-slate-900">
                          {booking.name}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${sc.bg} ${sc.text}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                          {booking.status.replace("_", " ")}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {booking.email}
                        {booking.firmName && ` · ${booking.firmName}`}
                        {booking.phone && ` · ${booking.phone}`}
                      </div>
                    </div>

                    {/* Date/Time */}
                    <div className="text-right flex-shrink-0 hidden sm:block">
                      <div className="text-sm font-semibold text-slate-900">
                        {formatDate(booking.date)}
                      </div>
                      <div className="text-xs text-slate-500">
                        {formatTime(booking.time)} ET
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="relative flex-shrink-0">
                      <button
                        onClick={() =>
                          setActionMenu(
                            actionMenu === booking._id ? null : booking._id
                          )
                        }
                        className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      {actionMenu === booking._id && (
                        <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl shadow-lg border border-slate-200 py-1 z-20">
                          {booking.status === "confirmed" && (
                            <>
                              <button
                                onClick={() =>
                                  updateBooking(booking._id, "completed")
                                }
                                className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                              >
                                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                Mark Completed
                              </button>
                              <button
                                onClick={() =>
                                  updateBooking(booking._id, "no_show")
                                }
                                className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                              >
                                <Clock className="w-4 h-4 text-amber-500" />
                                Mark No-Show
                              </button>
                              <button
                                onClick={() =>
                                  updateBooking(booking._id, "cancelled")
                                }
                                className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                              >
                                <XCircle className="w-4 h-4 text-red-500" />
                                Cancel
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => deleteBooking(booking._id)}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Support Tickets Section ──────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mt-8">
          <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-lg font-bold text-slate-900">Support Tickets</h2>
            <div className="flex items-center gap-2">
              {(["open", "replied", "resolved", "all"] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => { setTicketFilter(f); setActiveTicket(null); }}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all capitalize ${
                    ticketFilter === f
                      ? "bg-[#0F5132] text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          <div className="flex" style={{ minHeight: 300 }}>
            {/* Ticket list */}
            <div className={`${activeTicket ? "w-2/5 border-r border-slate-100" : "w-full"} overflow-y-auto`}>
              {tickets.length === 0 ? (
                <div className="p-12 text-center">
                  <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">No tickets found</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {tickets.map((ticket) => {
                    const ts = TICKET_STATUS[ticket.status] || TICKET_STATUS.open;
                    const isActive = activeTicket?._id === ticket._id;
                    return (
                      <button
                        key={ticket._id}
                        onClick={() => openTicketDetail(ticket)}
                        className={`w-full px-5 py-4 text-left hover:bg-slate-50 transition-colors ${
                          isActive ? "bg-emerald-50/50" : ""
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-semibold text-slate-900 truncate pr-2">
                            {ticket.subject}
                          </span>
                          <span
                            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0 ${ts.bg} ${ts.text}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${ts.dot}`} />
                            {ts.label}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500">
                          {ticket.userName} · {ticket.userEmail}
                          {ticket.firmName && ` · ${ticket.firmName}`}
                        </div>
                        <div className="text-[10px] text-slate-400 mt-1">
                          {timeAgo(ticket.updatedAt)} · {ticket.messages.length} message{ticket.messages.length !== 1 ? "s" : ""}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Ticket detail / thread */}
            {activeTicket && (
              <div className="w-3/5 flex flex-col">
                {/* Ticket header */}
                <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">{activeTicket.subject}</h3>
                    <p className="text-xs text-slate-500">
                      {activeTicket.userName} ({activeTicket.userEmail})
                      {activeTicket.firmName && ` — ${activeTicket.firmName}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {activeTicket.status !== "resolved" && (
                      <button
                        onClick={() => updateTicketStatus(activeTicket._id, "resolved")}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all flex items-center gap-1"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Resolve
                      </button>
                    )}
                    {activeTicket.status === "resolved" && (
                      <button
                        onClick={() => updateTicketStatus(activeTicket._id, "open")}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100 transition-all"
                      >
                        Reopen
                      </button>
                    )}
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: 320 }}>
                  {activeTicket.messages.map((msg, i) => (
                    <div
                      key={i}
                      className={`flex ${msg.sender === "admin" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                          msg.sender === "admin"
                            ? "bg-[#0F5132] text-white rounded-br-md"
                            : "bg-slate-100 text-slate-800 rounded-bl-md"
                        }`}
                      >
                        {msg.sender === "user" && (
                          <p className="text-[10px] font-bold text-slate-500 mb-1">{activeTicket.userName}</p>
                        )}
                        {msg.sender === "admin" && (
                          <p className="text-[10px] font-bold text-emerald-200 mb-1">You</p>
                        )}
                        <p className="whitespace-pre-wrap">{msg.text}</p>
                        <p className={`text-[10px] mt-1 ${msg.sender === "admin" ? "text-emerald-200" : "text-slate-400"}`}>
                          {timeAgo(msg.createdAt)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Reply input */}
                {activeTicket.status !== "resolved" && (
                  <div className="px-4 py-3 border-t border-slate-100">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={adminReply}
                        onChange={(e) => setAdminReply(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleAdminReply();
                          }
                        }}
                        placeholder="Type your reply..."
                        className="flex-1 px-3.5 py-2.5 text-sm rounded-xl border border-slate-200 focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/10"
                      />
                      <button
                        onClick={handleAdminReply}
                        disabled={sendingReply || !adminReply.trim()}
                        className="p-2.5 rounded-xl bg-[#0F5132] hover:bg-[#146C43] text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
